// Privacy values stream UI: the collection-matrix publish confirmation (B-31, PD-615), values hidden
// by the matrix and their reviewed purge (PB-16-03 / B-17, PD-610 – PD-614), the legal basis
// catalogue and per-value privacy settings (H-04 / B-14, PD-617 – PD-621). The API re-authorises and
// re-checks everything; these screens only show what the server says.
import type {
  CollectionMatrixPublishResult,
  LegalBasisListResponse,
  LegalBasisView,
  MinimisationImpactResponse,
  MinimisationListResponse,
  PrivacySettingsView,
  PurgeRunView,
} from '@passage/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { ApiRequestError, api, get, newIdempotencyKey } from '../api.ts';
import { useI18n } from '../i18n.tsx';
import { useSession } from '../session.tsx';
import { Dialog, ErrorState, ErrorSummary, Field, Loading } from './ui.tsx';

const Tag = ({ tone, children }: { tone: string; children: ReactNode }) => <span className={`badge ${tone}`}>{children}</span>;

/** A matrix item's label: built-in items from the catalogue, dictionary fields and documents by key. */
function useItemLabel(): (item: string) => string {
  const { tk } = useI18n();
  return (item) => (item.startsWith('field:') || item.startsWith('document:') ? item.slice(item.indexOf(':') + 1) : tk(`orgs.item.${item}`, undefined, item));
}

// ------------------------------------------------------------------ publish confirmation (B-31)

/**
 * Before the collection matrix is published: which visitor types and fields change, how many
 * existing records become hidden (and how many reappear). Publishing confirms that number; when
 * the data changed meanwhile the server refuses and the dialog shows the new impact.
 */
export function MatrixPublishDialog({ draftVersion, onClose, onDone }: { draftVersion: number; onClose: () => void; onDone: (r: CollectionMatrixPublishResult) => void }) {
  const { t, tk } = useI18n();
  const { scopeKey } = useSession();
  const label = useItemLabel();
  const q = useQuery({ queryKey: [...scopeKey, 'collection-matrix', 'impact', draftVersion], queryFn: () => get<MinimisationImpactResponse>('/api/v1/collection-matrix/draft/impact') });
  const [ack, setAck] = useState(false);
  const publish = useMutation({
    mutationFn: () => api<CollectionMatrixPublishResult>('POST', '/api/v1/collection-matrix/draft/publish', { expectedVersion: draftVersion, acknowledgedHidden: q.data!.hide.total }),
    onSuccess: onDone,
    onError: (e) => {
      if (e instanceof ApiRequestError && e.code === 'EVALUATION_STALE') {
        setAck(false);
        void q.refetch();
      }
    },
  });
  const d = q.data;
  const typeLabel = (code: string) => d?.visitorTypes.find((v) => v.code === code)?.label ?? code;
  const byItem = new Map<string, MinimisationImpactResponse['changes'][number][]>();
  for (const c of d?.changes ?? []) byItem.set(c.item, [...(byItem.get(c.item) ?? []), c]);
  const hidden = d?.hide.total ?? 0;
  return (
    <Dialog open title={d ? t('priv.pub.title', { n: d.draft.number }) : t('common.loading')} subtitle={t('priv.pub.subtitle')} onClose={onClose}>
      {q.isPending ? <Loading /> : q.isError ? <ErrorState error={q.error} retry={() => void q.refetch()} /> : d ? (
        <div className="stack">
          <p>{t('priv.pub.body')}</p>
          <ErrorSummary error={publish.error} fieldIds={{}} />
          <h3>{t('priv.pub.types')}</h3>
          {d.visitorTypes.length ? <ul>{d.visitorTypes.map((v) => <li key={v.code}>{v.label}</li>)}</ul> : <p className="tiny">{t('priv.pub.noTypes')}</p>}
          <h3>{t('priv.pub.items')}</h3>
          {byItem.size ? (
            <div className="tablewrap" tabIndex={0} aria-label={t('priv.pub.items')}>
              <table>
                <thead><tr><th scope="col">{t('orgs.cm.item')}</th><th scope="col">{t('priv.pub.change')}</th></tr></thead>
                <tbody>
                  {[...byItem].map(([item, cs]) => (
                    <tr key={item}>
                      <td><strong>{d.items.find((i) => i.item === item)?.label ?? label(item)}</strong></td>
                      <td>{cs.map((c) => <small key={`${c.category}|${c.purpose ?? ''}`}>{typeLabel(c.category)}{c.purpose ? <> · <bdi dir="ltr">{c.purpose}</bdi></> : null}: {tk(`orgs.mode.${c.from}`)} → {tk(`orgs.mode.${c.to}`)}</small>)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <p className="tiny">{t('priv.pub.noChanges')}</p>}
          <h3>{t('priv.pub.records')}</h3>
          {hidden > 0 ? (
            <div className="note warning" role="note">
              <strong>{t('priv.pub.hide', { count: hidden })}</strong>
              <ul>{Object.entries(d.hide.byItem).map(([item, n]) => <li key={item}>{d.items.find((i) => i.item === item)?.label ?? label(item)}: {n}</li>)}</ul>
              <span>{t('priv.pub.hideHow')}</span>
            </div>
          ) : <p className="tiny">{t('priv.pub.hideNone')}</p>}
          {d.restore.total > 0 ? <p className="tiny">{t('priv.pub.restore', { count: d.restore.total })}</p> : null}
          {hidden > 0 ? <label><input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} /> {t('priv.pub.ack', { count: hidden })}</label> : null}
          <div className="form-actions">
            <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
            <button className="button primary" type="button" disabled={publish.isPending || (hidden > 0 && !ack)} onClick={() => publish.mutate()}>{t('orgs.cm.publish')}</button>
          </div>
        </div>
      ) : null}
    </Dialog>
  );
}

// ------------------------------------------------------------------ hidden values and reviewed purge (B-17)

export function HiddenValuesPanel() {
  const { t, tk, dateTime } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const label = useItemLabel();
  const key = [...scopeKey, 'privacy', 'minimisation'];
  const q = useQuery({ queryKey: key, queryFn: () => get<MinimisationListResponse>('/api/v1/privacy/minimisation') });
  const [item, setItem] = useState('');
  const [dryRun, setDryRun] = useState(false);
  const [k, setK] = useState(newIdempotencyKey);
  const plan = useMutation({
    mutationFn: () => api<PurgeRunView>('POST', '/api/v1/privacy/minimisation/purges', { dryRun, ...(item ? { items: [item] } : {}) }, { idempotencyKey: k }),
    onSuccess: () => {
      setK(newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: [...scopeKey, 'privacy'] });
    },
  });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  const d = q.data;
  return (
    <section className="panel" aria-labelledby="priv-hidden-title">
      <div className="panel-head">
        <div>
          <h2 id="priv-hidden-title">{t('priv.hid.title')}</h2>
          <p>{t('priv.hid.intro')}</p>
        </div>
      </div>
      <div className="pad stack">
        {d.total === 0 ? <p className="empty">{t('priv.hid.empty')}</p> : (
          <>
            <p className="tiny">{t('priv.hid.total', { count: d.total })} · {Object.entries(d.summary).map(([i, n]) => `${label(i)}: ${n}`).join(' · ')}</p>
            <div className="tablewrap" tabIndex={0} aria-label={t('priv.hid.title')}>
              <table>
                <thead><tr><th scope="col">{t('orgs.cm.item')}</th><th scope="col">{t('priv.hid.record')}</th><th scope="col">{t('priv.hid.since')}</th><th scope="col">{t('priv.hid.restorable')}</th></tr></thead>
                <tbody>
                  {d.items.map((x) => (
                    <tr key={x.id}>
                      <td><strong>{label(x.item)}</strong><small>{tk(`pv.class.${x.recordClass}`)}</small></td>
                      <td>{tk(`priv.target.${x.target}`)}<small data-ltr>{x.recordId.slice(0, 8)}</small></td>
                      <td>{dateTime(x.hiddenAt)}<small>{t('priv.hid.byVersion', { n: x.hiddenByNumber })}</small></td>
                      <td>{x.restorable ? <Tag tone="blue">{t('priv.hid.canReturn')}</Tag> : <Tag tone="amber">{t('priv.hid.newerValue')}</Tag>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {d.nextCursor ? <p className="tiny">{t('priv.hid.more')}</p> : null}
          </>
        )}
        {d.canPlanPurge && d.total > 0 ? (
          <form className="formgrid" onSubmit={(e) => { e.preventDefault(); plan.mutate(); }}>
            <ErrorSummary error={plan.error} fieldIds={{}} />
            <Field id="priv-hid-item" label={t('priv.hid.purgeItem')}>
              {(p) => (
                <select {...p} value={item} onChange={(e) => setItem(e.target.value)}>
                  <option value="">{t('priv.hid.allItems')}</option>
                  {Object.keys(d.summary).map((i) => <option key={i} value={i}>{label(i)}</option>)}
                </select>
              )}
            </Field>
            <label><input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} /> {t('pv.pg.dryRun')}</label>
            <div className="actions"><button className="button primary" type="submit" disabled={plan.isPending}>{t('priv.hid.plan')}</button></div>
            {plan.data ? <p className="note" role="status">{t('priv.hid.planned')}</p> : null}
            <p className="tiny">{t('priv.hid.fourEyes')}</p>
          </form>
        ) : null}
      </div>
    </section>
  );
}

// ------------------------------------------------------------------ legal basis catalogue (B-14)

export function useLegalBases() {
  const { scopeKey } = useSession();
  return useQuery({ queryKey: [...scopeKey, 'privacy', 'legal-bases'], queryFn: () => get<LegalBasisListResponse>('/api/v1/privacy/legal-bases') });
}

export function LegalBasesPanel() {
  const { t, locale } = useI18n();
  const q = useLegalBases();
  const [edit, setEdit] = useState<LegalBasisView | 'new' | null>(null);
  const [retire, setRetire] = useState<LegalBasisView | null>(null);
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  const loc = (x: { en: string; ar?: string } | null) => (x ? (locale === 'ar' && x.ar ? x.ar : x.en) : null);
  return (
    <section className="panel" aria-labelledby="priv-lb-title">
      <div className="panel-head">
        <div><h2 id="priv-lb-title">{t('priv.lb.title')}</h2><p>{t('priv.lb.intro')}</p></div>
        {q.data.canManage ? <div className="actions"><button className="button small" type="button" onClick={() => setEdit('new')}>{t('priv.lb.add')}</button></div> : null}
      </div>
      <div className="tablewrap" tabIndex={0} aria-label={t('priv.lb.title')}>
        <table>
          <thead><tr><th scope="col">{t('pv.pur.basis')}</th><th scope="col">{t('priv.lb.wording')}</th><th scope="col">{t('priv.lb.consent')}</th><th scope="col">{t('pv.req.col.status')}</th><th scope="col"><span className="visually-hidden">{t('pv.col.action')}</span></th></tr></thead>
          <tbody>
            {q.data.items.map((b) => (
              <tr key={b.code}>
                <td><strong dir="auto">{loc(b.label)}</strong><small data-ltr>{b.code}</small></td>
                <td>{b.wording ? <span dir="auto">{loc(b.wording)}</span> : <Tag tone="amber">{t('priv.lb.noWording')}</Tag>}</td>
                <td>{b.requiresConsent ? t('common.yes') : t('common.no')}</td>
                <td>
                  <Tag tone={b.status === 'active' ? 'green' : ''}>{t(b.status === 'active' ? 'priv.lb.active' : 'priv.lb.retired')}</Tag>
                  <small>{b.source === 'engineering_default' ? t('pv.ret.engineeringDefault') : t('priv.lb.version', { n: b.number })}{b.usedBy ? ` · ${t('priv.lb.usedBy', { count: b.usedBy })}` : ''}</small>
                </td>
                <td>
                  {q.data.canManage ? (
                    <div className="actions">
                      <button className="button small" type="button" onClick={() => setEdit(b)}>{t('priv.lb.edit')}</button>
                      {b.status === 'active' && b.usedBy === 0 ? <button className="button small quiet" type="button" onClick={() => setRetire(b)}>{t('priv.lb.retire')}</button> : null}
                    </div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="pad tiny">{t('priv.lb.noInvent')}</p>
      {edit ? <BasisDialog basis={edit === 'new' ? null : edit} onClose={() => setEdit(null)} onDone={() => { setEdit(null); void q.refetch(); }} /> : null}
      {retire ? <RetireBasisDialog basis={retire} onClose={() => setRetire(null)} onDone={() => { setRetire(null); void q.refetch(); }} /> : null}
    </section>
  );
}

function BasisDialog({ basis, onClose, onDone }: { basis: LegalBasisView | null; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [f, setF] = useState({ code: basis?.code ?? '', labelEn: basis?.label.en ?? '', labelAr: basis?.label.ar ?? '', wordingEn: basis?.wording?.en ?? '', wordingAr: basis?.wording?.ar ?? '', requiresConsent: basis?.requiresConsent ?? false, note: '' });
  const [k] = useState(newIdempotencyKey);
  const save = useMutation({
    mutationFn: () =>
      api('PUT', `/api/v1/privacy/legal-bases/${encodeURIComponent(f.code)}`, {
        expectedNumber: basis?.number ?? 0,
        label: { en: f.labelEn, ...(f.labelAr ? { ar: f.labelAr } : {}) },
        wording: f.wordingEn ? { en: f.wordingEn, ...(f.wordingAr ? { ar: f.wordingAr } : {}) } : null,
        requiresConsent: f.requiresConsent,
        note: f.note || null,
      }, { idempotencyKey: k }),
    onSuccess: onDone,
  });
  const fe = save.error instanceof ApiRequestError ? save.error.fields : {};
  return (
    <Dialog open title={basis ? t('priv.lb.editTitle', { code: basis.code }) : t('priv.lb.add')} subtitle={t('priv.lb.dialogHint')} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        <ErrorSummary error={save.error} fieldIds={{ requiresConsent: 'priv-lb-consent' }} />
        <div className="formgrid">
          {basis ? null : <Field id="priv-lb-code" label={t('priv.lb.code')} hint={t('priv.lb.codeHint')}>{(p) => <input {...p} dir="ltr" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.trim() })} />}</Field>}
          <Field label={t('priv.lb.labelEn')}>{(p) => <input {...p} lang="en" dir="ltr" value={f.labelEn} onChange={(e) => setF({ ...f, labelEn: e.target.value })} />}</Field>
          <Field label={t('priv.lb.labelAr')}>{(p) => <input {...p} lang="ar" dir="rtl" value={f.labelAr} onChange={(e) => setF({ ...f, labelAr: e.target.value })} />}</Field>
          <Field label={t('priv.lb.wordingEn')} full>{(p) => <textarea {...p} lang="en" dir="ltr" rows={4} value={f.wordingEn} onChange={(e) => setF({ ...f, wordingEn: e.target.value })} />}</Field>
          <Field label={t('priv.lb.wordingAr')} full>{(p) => <textarea {...p} lang="ar" dir="rtl" rows={4} value={f.wordingAr} onChange={(e) => setF({ ...f, wordingAr: e.target.value })} />}</Field>
          <Field label={t('common.reason')} hint={t('priv.lb.noteHint')}>{(p) => <input {...p} dir="auto" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />}</Field>
        </div>
        <label id="priv-lb-consent"><input type="checkbox" checked={f.requiresConsent} onChange={(e) => setF({ ...f, requiresConsent: e.target.checked })} /> {t('priv.lb.requiresConsent')}</label>
        {fe.requiresConsent ? <p className="field-error">{fe.requiresConsent}</p> : null}
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
          <button className="button primary" type="submit" disabled={save.isPending || !f.code || !f.labelEn}>{t('common.save')}</button>
        </div>
      </form>
    </Dialog>
  );
}

function RetireBasisDialog({ basis, onClose, onDone }: { basis: LegalBasisView; onClose: () => void; onDone: () => void }) {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [k] = useState(newIdempotencyKey);
  const retire = useMutation({ mutationFn: () => api('POST', `/api/v1/privacy/legal-bases/${encodeURIComponent(basis.code)}/retire`, { expectedNumber: basis.number, reason }, { idempotencyKey: k }), onSuccess: onDone });
  return (
    <Dialog open title={t('priv.lb.retireTitle', { code: basis.code })} onClose={onClose}>
      <form className="stack" onSubmit={(e) => { e.preventDefault(); retire.mutate(); }}>
        <p>{t('priv.lb.retireBody')}</p>
        <ErrorSummary error={retire.error} fieldIds={{}} />
        <Field id="priv-lb-reason" label={t('common.reason')}>{(p) => <input {...p} dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} />}</Field>
        <div className="form-actions">
          <button className="button" type="button" onClick={onClose}>{t('common.cancel')}</button>
          <button className="button primary danger" type="submit" disabled={retire.isPending || reason.trim().length < 3}>{t('priv.lb.retire')}</button>
        </div>
      </form>
    </Dialog>
  );
}

// ------------------------------------------------------------------ privacy settings, per value (B-14)

type NumKey = 'requestResponseDays' | 'requestExtensionDays' | 'exportDownloadDays' | 'anonymityThreshold' | 'reportExportHours';

/**
 * Request deadlines and safeguards (PB-16-08, PD-256) with the export windows: each value says
 * whether it is still the engineering default; saving sends only the values changed; a value can be
 * returned to its engineering default (PD-618).
 */
export function PrivacySettingsPanel({ canManage }: { canManage: boolean }) {
  const { t, tk } = useI18n();
  const { scopeKey } = useSession();
  const qc = useQueryClient();
  const key = [...scopeKey, 'privacy', 'settings'];
  const q = useQuery({ queryKey: key, queryFn: () => get<PrivacySettingsView>('/api/v1/privacy/settings') });
  const [patch, setPatch] = useState<Partial<Record<NumKey | 'purgeFourEyes', number | boolean | null>>>({});
  const [k, setK] = useState(newIdempotencyKey);
  const save = useMutation({
    mutationFn: (body: typeof patch) => api('PUT', '/api/v1/privacy/settings', { expectedVersion: q.data!.version, ...body }, { idempotencyKey: k }),
    onSuccess: () => {
      setPatch({});
      setK(newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: key });
    },
  });
  if (!q.data) return q.isError ? <ErrorState error={q.error} /> : <Loading />;
  const s = q.data;
  const source = (k2: string) => (s.sources[k2] === 'organisation' ? <Tag tone="green">{t('priv.set.organisation')}</Tag> : <Tag tone="amber">{t('pv.ret.engineeringDefault')}</Tag>);
  const reset = (k2: NumKey | 'purgeFourEyes') => (canManage && s.sources[k2] === 'organisation' ? <button className="button small quiet" type="button" disabled={save.isPending} onClick={() => save.mutate({ [k2]: null })}>{t('priv.set.reset')}</button> : null);
  const value = (k2: NumKey): string => {
    const v = k2 in patch ? patch[k2] : s[k2];
    return v === null || v === undefined ? '' : String(v);
  };
  const num = (k2: NumKey, hint?: string) => (
    <Field label={tk(k2 === 'reportExportHours' ? 'priv.set.reportExportHours' : `pv.set.${k2}`)} {...(hint ? { hint } : {})}>
      {(p) => (
        <span className="stack">
          <input {...p} type="number" inputMode="numeric" disabled={!canManage} value={value(k2)} onChange={(e) => setPatch({ ...patch, [k2]: e.target.value === '' ? (k2 === 'reportExportHours' ? null : (s[k2] as number)) : Number(e.target.value) })} />
          <span>{source(k2)} {reset(k2)}</span>
        </span>
      )}
    </Field>
  );
  const fourEyes = (patch.purgeFourEyes ?? s.purgeFourEyes) as boolean;
  return (
    <section className="panel" aria-labelledby="pv-set-title">
      <div className="panel-head"><div><h2 id="pv-set-title">{t('pv.set.title')}</h2><p>{t('priv.set.intro')}</p></div></div>
      <form className="pad stack" onSubmit={(e) => { e.preventDefault(); save.mutate(patch); }}>
        <ErrorSummary error={save.error} fieldIds={{}} />
        <div className="formgrid">
          {num('requestResponseDays')}
          {num('requestExtensionDays')}
          {num('exportDownloadDays')}
          {num('reportExportHours', t('priv.set.reportExportHint', { hours: s.installationReportExportHours }))}
          {num('anonymityThreshold')}
        </div>
        <span>
          <label><input type="checkbox" disabled={!canManage} checked={fourEyes} onChange={(e) => setPatch({ ...patch, purgeFourEyes: e.target.checked })} /> {t('pv.set.purgeFourEyes')}</label> {source('purgeFourEyes')} {reset('purgeFourEyes')}
        </span>
        <p className="tiny">{t('pv.set.noAutoApply')}</p>
        {canManage ? <div className="actions"><button className="button" type="submit" disabled={!Object.keys(patch).length || save.isPending}>{t('common.save')}</button><span className="tiny">{t('priv.set.saveHint')}</span></div> : null}
      </form>
    </section>
  );
}
